<script lang="ts">
	import { sendGTM } from '$lib/analytics'
	import { ACADEMY_CONTACTS, SHOWROOM_VARS } from '$lib/consts'
	import NameCard from '$lib/modules/contact/NameCard.svelte'
	import Socials from '$lib/modules/contact/Socials.svelte'
	import {
		MEETING,
		WOLTADVISOR,
		FINANCING,
		PHONE_CONSULTATIONS,
		SERVICE,
		GRANTS
	} from '$lib/routes'
	import { t, tTag } from '$lib/translations'
	import { Button, Separator } from '$lib/ui-woltair/components'
	import type { PageData } from './$types'
	import { PUBLIC_WOLTAIR_WEB_CODE } from '$env/static/public'

	export let data: PageData
</script>

<section class="page-wrapper py-16">
	<div class="cont flex flex-col lg:flex-row">
		<div class="mb-10 lg:mb-0 lg:w-2/5">
			<h1 class="mt-0 !text-6.5xl-res tracking-normal">
				{t('web.CONTACT.header.heading')}
			</h1>
			{#if data.featureConfig.phoneNumber}
				<Button
					on:click={() => sendGTM('call_click')}
					href="tel:{tTag('web.CONTACT.header.button.link')}"
					icon={{
						src: '/images/icons/icon-phone.svg',
						alt: t('web.CONTACT.header.button.img.alt')
					}}
					green>{t('web.CONTACT.header.button.text')}</Button
				>
			{/if}
			<p class="text-gray text-lg leading-normal text-neutral-500 lg:max-w-sm">
				{t('web.CONTACT.header.mail.text')}
				<a class="text-red-600" href="mailto:{tTag('web.CONTACT.header.mail.address')}"
					>{t('web.CONTACT.header.mail.address')}</a
				>
			</p>

			<p class="text-gray text-lg leading-normal text-neutral-500 lg:max-w-sm">
				{t('web.CONTACT.header.media.text')}
				<a class="text-red-600" href="mailto:{tTag('web.CONTACT.header.media.mail')}"
					>{t('web.CONTACT.header.media.mail')}</a
				>
			</p>
			{#if new Date().getTime() < new Date(2022, 6, 15).getTime()}
				<p class="mt-6 font-semibold text-red-600 lg:max-w-sm">
					{t('web.CONTACT.header.callCenter.text')}
				</p>
			{/if}
		</div>
		{#if data.featureConfig.meeting.hvac || data.featureConfig.meeting.pv || data.featureConfig.service || data.featureConfig.grants.hvac || data.featureConfig.grants.pv}
			<div class="right chat">
				<div class="yellow-message mb-2 px-12 py-4 shadow">
					<img src="/images/avatars/worker.webp" alt="Pracovník" />
					<p class="text-lg-res font-semibold">{t('web.CONTACT.header.chat.box.header')}</p>
					<p class="text-lg-res">{t('web.CONTACT.header.chat.box.text')}</p>
				</div>
				<div class="mt-5 flex flex-wrap gap-3">
					{#if data.featureConfig.meeting.hvac || data.featureConfig.meeting.pv}
						<a
							class="btn btn-outline-accent"
							on:click={() => sendGTM('online_meet')}
							href="/{MEETING}">{t('web.CONTACT.header.chat.meeting.text')}</a
						>
					{/if}
					{#if data.featureConfig.service}
						<a class="btn btn-outline-accent" href="/{SERVICE}"
							>{t('web.CONTACT.header.chat.service.text')}</a
						>
					{/if}
					{#if data.featureConfig.grants.hvac || data.featureConfig.grants.pv}
						<a class="btn btn-outline-accent" href="/{GRANTS}"
							>{t('web.CONTACT.header.chat.grants.text')}</a
						>
					{/if}
					{#if data.featureConfig.woltadvisor.pv || data.featureConfig.woltadvisor.hvac}
						<a class="btn btn-outline-accent" href="/{WOLTADVISOR}"
							>{t('web.CONTACT.header.chat.calculator.text')}</a
						>
					{/if}
					{#if data.featureConfig.financing.pv || data.featureConfig.financing.hvac}
						<a class="btn btn-outline-accent" href="/{FINANCING}"
							>{t('web.CONTACT.header.chat.financing.text')}</a
						>
					{/if}
					{#if PHONE_CONSULTATIONS}
						<a
							class="btn btn-outline-accent"
							href="/{PHONE_CONSULTATIONS}"
							on:click={() => sendGTM('order_call')}
						>
							{t('web.CONTACT.header.chat.consultation.text')}
						</a>
					{/if}
					<!--
					<button class="btn btn-outline-accent" on:click={() => window?.mluvii?.openChat()}
						>{t('web.CONTACT.header.chat.openChat.text')}</button
					>-->
				</div>
			</div>
		{:else}
			<div class="max-w-1/2">
				<img src="/images/persons/woltair-people-skew.webp" alt="Pracovník" class="worker" />
			</div>
		{/if}
	</div>
</section>
{#if data.featureConfig.showroom}
	<section class="page-fullwidth bg-white pb-16">
		<Separator align="center" />
		<div class="cont page-wrapper mt-12 flex flex-col gap-28 lg:flex-row">
			<div class="flex-1">
				<h2 class="mb-6 !text-4.5xl-res !font-extrabold tracking-tight">
					{t('web.CONTACT.showroom.heading')}
				</h2>
				<p class="text-lg-res text-neutral-600">
					{t('web.CONTACT.showroom.text')}
				</p>
				<div class="box-contact">
					<img src="/images/icons/icon-map.svg" alt={t('web.CONTACT.showroom.img.alt')} />
					<div>
						<p class="text-lg">{t('web.CONTACT.showroom.address')}</p>
						<a href={SHOWROOM_VARS[data.organization.lang].mapUrl} class="text-red-600"
							>{t('web.CONTACT.showroom.map.text')}</a
						>
					</div>
				</div>
				{#if data.featureConfig.phoneNumber}
					<div class="mt-6 text-neutral-600">
						{t('web.CONTACT.showroom.phone.callUs')}
						<div class="mb-2 mt-1">
							<a
								href={`tel:${SHOWROOM_VARS[data.organization.lang].mapUrl}`}
								class="font-semibold text-red-600">{t('web.CONTACT.showroom.phone.text')}</a
							>
						</div>
					</div>
				{/if}
			</div>
			<div class="flex-1">
				{#if data.featureConfig.academy}
					<h3 class="mb-6 !text-4.5xl-res !font-extrabold tracking-tight">
						{t('web.CONTACT.academy.heading')}
					</h3>
					<NameCard card={ACADEMY_CONTACTS[PUBLIC_WOLTAIR_WEB_CODE]?.card} cls="mt-3 mb-5" />
				{/if}
				<Socials />
			</div>
		</div>
	</section>
	<section class="page-fullwidth bg-white pb-10">
		<div class="img page-wrapper">
			<img src="/images/persons/woltair-people.webp" alt={t('web.CONTACT.academy.img.alt')} />
		</div>
	</section>
{/if}

<style lang="scss">
	.cont {
		.right {
			@media only screen and (max-width: 760px) {
				width: 100%;
			}

			&.chat {
				max-width: 600px;
			}

			img {
				width: 100%;
			}

			.yellow-message {
				position: relative;
				background: #fff7d8;
				border-radius: 5px;

				img {
					width: 50px;
					position: absolute;
					top: 50%;
					transform: translateY(-50%);
					left: 20px;
					margin-top: 0;
				}

				p {
					margin-left: 40px;
				}
			}
		}
	}

	.box-contact {
		margin-top: 50px;
		display: flex;
		padding: 20px;
		align-items: center;
		border-radius: 5px;
		box-shadow: 0 0 30px 0 rgba(0, 0, 0, 0.175);

		img {
			margin-right: 20px;
		}

		div {
			p {
				margin-top: 0;
				margin-bottom: 5px;
			}
		}
	}
</style>
